import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createNotificationApi } from "./notificationApi";
import { useTaskEvents } from "../tasks/hooks/useTaskEvents";
import "./notificationBell.css";

function playChime() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.value = 0.05;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        setTimeout(() => {
            osc.stop();
            ctx.close();
        }, 140);
    } catch {
        /* ignore */
    }
}

const NotificationBell = ({ apiUrl, getToken }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [sound, setSound] = useState(true);
    const boxRef = useRef(null);
    const api = createNotificationApi({ apiUrl, getToken });

    const refresh = useCallback(async () => {
        try {
            const [list, unread] = await Promise.all([
                api.list({ limit: 8 }),
                api.unreadCount(),
            ]);
            setItems(list.data || []);
            setCount(unread);
        } catch {
            /* ignore */
        }
    }, [apiUrl, getToken]);

    useEffect(() => {
        refresh();
        api.getPreferences()
            .then((p) => setSound(p?.sound_enabled !== false))
            .catch(() => null);
    }, [refresh]);

    useTaskEvents({
        apiUrl,
        getToken,
        onEvent: (payload) => {
            if (payload?.type === "notification:new") {
                refresh();
                if (sound) playChime();
            }
        },
    });

    useEffect(() => {
        const onDoc = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const openItem = async (item) => {
        if (!item.read_at) await api.markRead([item.id]);
        if (item.task_id) await api.logOpened(item.task_id);
        setOpen(false);
        navigate(item.deep_link || `/tasks?taskId=${item.task_id || ""}`);
        refresh();
    };

    return (
        <div className="notif-bell" ref={boxRef}>
            <button
                type="button"
                className="notif-bell__btn"
                aria-label="Notifications"
                onClick={() => {
                    setOpen((v) => !v);
                    refresh();
                }}
            >
                <i className="fa-regular fa-bell" />
                {count > 0 && <span className="notif-bell__count">{count > 99 ? "99+" : count}</span>}
            </button>
            {open && (
                <div className="notif-bell__menu">
                    <div className="notif-bell__head">
                        <strong>Notifications</strong>
                        {count > 0 && (
                            <button type="button" onClick={() => api.markRead(null, true).then(refresh)}>
                                Mark all read
                            </button>
                        )}
                    </div>
                    <ul>
                        {items.length === 0 && <li className="notif-bell__empty">No notifications</li>}
                        {items.map((item) => (
                            <li key={item.id}>
                                <button
                                    type="button"
                                    className={item.read_at ? "" : "is-unread"}
                                    onClick={() => openItem(item)}
                                >
                                    <span>{item.title}</span>
                                    <small>{item.body}</small>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <Link to="/notifications" className="notif-bell__all" onClick={() => setOpen(false)}>
                        Open notification center
                    </Link>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
